import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft,
  Sparkles,
  Send,
  Save,
  Wand2,
  CheckCircle2,
  AlertCircle,
  Paperclip,
  Upload,
  X,
  Trash2,
  Pencil,
} from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { EmailPreview } from "@/components/EmailPreview";

const MODELS = [
  { value: "claude-sonnet-4-6", label: "Claude Sonnet 4.6 (Anthropic)" },
  { value: "gpt-5.6-sol", label: "GPT-5.6 Sol (OpenAI, le + puissant)" },
  { value: "gpt-5.6-terra", label: "GPT-5.6 Terra (OpenAI, équilibré)" },
  { value: "gpt-5.6-luna", label: "GPT-5.6 Luna (OpenAI, rapide)" },
  { value: "gpt-5.5", label: "GPT-5.5 (OpenAI)" },
  { value: "gemini-2.5-pro", label: "Gemini 2.5 Pro (Vertex AI, UE)" },
];
const TONES = [
  { value: "chaleureux", label: "Chaleureux" },
  { value: "empathique", label: "Empathique" },
  { value: "professionnel", label: "Professionnel" },
  { value: "formel", label: "Formel" },
];
const LENGTHS = [
  { value: "court", label: "Court" },
  { value: "moyen", label: "Moyen" },
  { value: "long", label: "Long" },
];
const LANGS = [
  { value: "fr", label: "🇫🇷 Français" },
  { value: "en", label: "🇬🇧 English" },
  { value: "es", label: "🇪🇸 Español" },
];

const QUILL_MODULES = {
  toolbar: [
    [{ size: ["small", false, "large", "huge"] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["clean"],
  ],
};
const QUILL_FORMATS = ["size", "bold", "italic", "underline", "list"];

const mdInline = (s) =>
  s
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/__(.+?)__/g, "<strong>$1</strong>")
    .replace(/(^|\s)\*([^*\s][^*]*?)\*(?=\s|$|[.,;:!?)])/g, "$1<em>$2</em>")
    .replace(/(^|\s)_([^_\s][^_]*?)_(?=\s|$|[.,;:!?)])/g, "$1<em>$2</em>");

const textToHtml = (text) => {
  if (!text) return "";
  if (/<[a-z][\s\S]*>/i.test(text)) {
    // corps déjà en HTML : répare quand même les mots coupés au tiret (fois-<br>ci -> fois-ci)
    return text.replace(/-\s*<br\s*\/?>\s*(?=[a-zà-öø-ÿ])/gi, "-").replace(/-\n(?=[a-zà-öø-ÿ])/g, "-");
  }
  // tiret en fin de ligne + minuscule : mot coupé par le modèle (fois-ci, e-mail, vous-même)
  text = text.replace(/-\n(?=[a-zà-öø-ÿ])/g, "-");
  const out = [];
  let list = null;
  let para = [];
  const closeList = () => {
    if (list) {
      out.push(`</${list}>`);
      list = null;
    }
  };
  const joinWrapped = (lines) => {
    let buf = lines[0];
    for (const nxt of lines.slice(1)) {
      const end = buf.replace(/\s+$/, "");
      const start = nxt.replace(/^\s+/, "");
      // mot composé coupé au tiret (e-mail, rendez-vous, fois-ci), sinon jointure avec espace
      buf = /-$/.test(end) && /^[a-zà-öø-ÿ]/.test(start) ? end + start : end + " " + start;
    }
    return buf;
  };
  const flushPara = () => {
    if (!para.length) return;
    // lignes coupées à largeur fixe par certains modèles IA -> refusionner en un paragraphe fluide
    const hardWrapped = para.length > 1 && para.slice(0, -1).every((l) => l.trim().length >= 55);
    if (hardWrapped) {
      out.push(`<p>${mdInline(joinWrapped(para))}</p>`);
    } else {
      out.push(`<p>${para.map((l) => mdInline(l.trim())).join("<br>")}</p>`);
    }
    para = [];
  };
  for (const raw of text.split("\n")) {
    const line = raw.trim();
    if (!line) {
      flushPara();
      closeList();
      continue;
    }
    const h = line.match(/^#{1,6}\s+(.*)/);
    const ul = line.match(/^[-*]\s+(.*)/);
    const ol = line.match(/^\d+[.)]\s+(.*)/);
    if (h) {
      flushPara();
      closeList();
      out.push(`<p><strong>${mdInline(h[1])}</strong></p>`);
    } else if (ul) {
      flushPara();
      if (list !== "ul") {
        closeList();
        out.push("<ul>");
        list = "ul";
      }
      out.push(`<li>${mdInline(ul[1])}</li>`);
    } else if (ol) {
      flushPara();
      if (list !== "ol") {
        closeList();
        out.push("<ol>");
        list = "ol";
      }
      out.push(`<li>${mdInline(ol[1])}</li>`);
    } else {
      closeList();
      para.push(raw);
    }
  }
  flushPara();
  closeList();
  return out.join("");
};

const SessionDetail = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);

  const [model, setModel] = useState("claude-sonnet-4-6");
  const [tone, setTone] = useState("chaleureux");
  const [length, setLength] = useState("moyen");
  const [language, setLanguage] = useState("fr");
  const [instructions, setInstructions] = useState("");
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");

  const fillVars = (text) => {
    const name = patient?.name || "";
    const prenom = name.split(" ")[0] || "";
    const dateStr = session?.session_date
      ? format(parseISO(session.session_date), "d MMMM yyyy", { locale: fr })
      : "";
    return (text || "")
      .replaceAll("{prénom}", prenom)
      .replaceAll("{prenom}", prenom)
      .replaceAll("{nom}", name)
      .replaceAll("{date}", dateStr)
      .replaceAll("{thérapeute}", user?.name || "")
      .replaceAll("{therapeute}", user?.name || "");
  };

  const applyTemplate = (id) => {
    setSelectedTemplate(id);
    const t = templates.find((t) => t.id === id);
    if (!t) return;
    if (t.type === "texte") {
      if (t.subject) setSubject(fillVars(t.subject));
      setBody(textToHtml(fillVars(t.body || "")));
      toast.success(`Modèle texte « ${t.name} » appliqué à l'e-mail`);
    } else {
      setInstructions(t.instructions || "");
      if (t.tone) setTone(t.tone);
      if (t.length) setLength(t.length);
      toast.success(`Modèle « ${t.name} » appliqué`);
    }
  };

  const saveAsTemplate = async () => {
    const name = window.prompt("Nom du modèle (ex. « Suivi exposition », « Bilan TCC ») :");
    if (!name || !name.trim()) return;
    try {
      const res = await api.post("/templates", { name, instructions, tone, length });
      setTemplates((l) => [...l, res.data]);
      setSelectedTemplate(res.data.id);
      toast.success("Modèle enregistré");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'enregistrement du modèle");
    }
  };

  const deleteTemplate = async () => {
    const t = templates.find((t) => t.id === selectedTemplate);
    if (!t) return;
    if (!window.confirm(`Supprimer le modèle « ${t.name} » ?`)) return;
    try {
      await api.delete(`/templates/${t.id}`);
      setTemplates((l) => l.filter((x) => x.id !== t.id));
      setSelectedTemplate("");
      toast.success("Modèle supprimé");
    } catch (e) {
      toast.error("Erreur de suppression");
    }
  };

  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const [attachments, setAttachments] = useState([]); // File objects (from the therapist's computer)
  const fileInputRef = useRef(null);

  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [copyMe, setCopyMe] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [sender, setSender] = useState(null);

  const ccPreview = () => {
    const list = cc.split(",").map((e) => e.trim()).filter(Boolean);
    if (copyMe && user?.email && !list.includes(user.email)) list.push(user.email);
    return list;
  };

  const [transcriptDraft, setTranscriptDraft] = useState("");
  const [savingTranscript, setSavingTranscript] = useState(false);
  const [editingTranscript, setEditingTranscript] = useState(false);

  const startEditTranscript = () => {
    setTranscriptDraft(session?.transcript || "");
    setEditingTranscript(true);
  };

  const saveTranscript = async () => {
    if (!transcriptDraft.trim()) {
      toast.error("Collez d'abord la transcription");
      return;
    }
    setSavingTranscript(true);
    try {
      const res = await api.put(`/sessions/${sessionId}/transcript`, { transcript: transcriptDraft });
      setSession(res.data);
      setEditingTranscript(false);
      toast.success("Transcription enregistrée");
    } catch (e) {
      toast.error("Erreur d'enregistrement de la transcription");
    } finally {
      setSavingTranscript(false);
    }
  };

  const onFilePicked = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    const valid = [];
    for (const f of picked) {
      if (f.size > 10 * 1024 * 1024) {
        toast.error(`Fichier trop volumineux (max 10 Mo) : ${f.name}`);
        continue;
      }
      valid.push(f);
    }
    if (valid.length) setAttachments((a) => [...a, ...valid]);
  };

  const load = async () => {
    try {
      const res = await api.get(`/sessions/${sessionId}`);
      setSession(res.data);
      setSubject(res.data.email_subject || "");
      setBody(textToHtml(res.data.email_body || ""));
      if (res.data.model_used) setModel(res.data.model_used);
      if (res.data.tone) setTone(res.data.tone);
      if (res.data.length) setLength(res.data.length);
      const p = await api.get(`/patients/${res.data.patient_id}`);
      setPatient(p.data);
      setLanguage(res.data.language || p.data.language || "fr");
      api.get("/templates").then((t) => setTemplates(t.data)).catch(() => {});
      api.get("/settings/smtp").then((s) => setSender(s.data)).catch(() => {});
    } catch (e) {
      toast.error("Séance introuvable");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await api.post(`/sessions/${sessionId}/generate`, { model, tone, length, language, instructions });
      setSession(res.data);
      setSubject(res.data.email_subject || "");
      setBody(textToHtml(res.data.email_body || ""));
      toast.success("E-mail généré");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de génération");
    } finally {
      setGenerating(false);
    }
  };

  const saveDraft = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/sessions/${sessionId}/email`, { email_subject: subject, email_body: body });
      setSession(res.data);
      toast.success("Brouillon enregistré");
    } catch (e) {
      toast.error("Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const deleteSession = async () => {
    if (!window.confirm("Supprimer définitivement cette séance ?")) return;
    try {
      await api.delete(`/sessions/${sessionId}`);
      toast.success("Séance supprimée");
      navigate(patient ? `/patients/${patient.id}` : "/dashboard");
    } catch (e) {
      toast.error("Erreur de suppression");
    }
  };

  const send = async () => {
    if (!patient?.email) {
      toast.error("Ajoutez d'abord une adresse e-mail au patient");
      return;
    }
    setSending(true);
    try {
      await api.put(`/sessions/${sessionId}/email`, { email_subject: subject, email_body: body });
      const fd = new FormData();
      attachments.forEach((f) => fd.append("files", f));
      const ccList = cc.split(",").map((e) => e.trim()).filter(Boolean);
      if (copyMe && user?.email && !ccList.includes(user.email)) ccList.push(user.email);
      fd.append("cc", ccList.join(","));
      fd.append("bcc", bcc);
      const res = await api.post(`/sessions/${sessionId}/send`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`E-mail envoyé à ${res.data.to}${res.data.cc?.length ? ` (+ ${res.data.cc.length} en copie)` : ""}${res.data.attachments ? ` (${res.data.attachments} pièce(s) jointe(s))` : ""}`);
      setSession((s) => ({ ...s, status: "envoye", sent_at: res.data.sent_at }));
      setShowPreview(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'envoi");
    } finally {
      setSending(false);
    }
  };

  if (loading || !session) {
    return (
      <AppLayout>
        <div className="h-40 bg-white/60 rounded-2xl border border-[#EAE6DF] animate-pulse" />
      </AppLayout>
    );
  }

  const hasEmail = !!(session?.email_body || body.replace(/<[^>]+>/g, "").trim());

  return (
    <AppLayout>
      <button
        onClick={() => patient && navigate(`/patients/${patient.id}`)}
        data-testid="back-to-patient-btn"
        className="flex items-center gap-1.5 text-sm text-[#757169] hover:text-[#2D2C28] transition-colors mb-5"
      >
        <ArrowLeft className="w-4 h-4" /> Retour à la fiche patient
      </button>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-[#2D2C28]">
            Séance du {format(parseISO(session.session_date), "d MMMM yyyy", { locale: fr })}
          </h1>
          <p className="text-[#757169] text-sm mt-1">
            {patient?.name}
            {patient?.email ? ` · ${patient.email}` : ""}
          </p>
        </div>
        <div className="flex items-center gap-3 self-start">
          {session.status === "envoye" && (
            <Badge className="rounded-full bg-[#7B968B]/20 text-[#5e7a6e] border-0">
              <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Envoyé le{" "}
              {session.sent_at && format(parseISO(session.sent_at), "d MMM à HH:mm", { locale: fr })}
            </Badge>
          )}
          <Button
            variant="outline"
            onClick={deleteSession}
            data-testid="delete-session-btn"
            className="rounded-xl border-[#EAE6DF] text-[#B56B6B] hover:bg-[#B56B6B]/5"
          >
            <Trash2 className="w-4 h-4 mr-2" /> Supprimer
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: transcript + controls */}
        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-[#EAE6DF] p-6 shadow-[0_4px_20px_rgba(45,44,40,0.04)]">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-medium text-[#2D2C28]">Transcription de la séance</h3>
              {session.transcript && !editingTranscript && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={startEditTranscript}
                  data-testid="edit-transcript-btn"
                  className="rounded-lg border-[#EAE6DF] text-[#575349] h-8 text-xs"
                >
                  <Pencil className="w-3.5 h-3.5 mr-1.5" /> Modifier
                </Button>
              )}
            </div>
            {session.transcript && !editingTranscript ? (
              <div className="max-h-56 overflow-y-auto bg-[#FBF9F6] rounded-xl p-4 text-sm text-[#575349] leading-relaxed whitespace-pre-wrap">
                {session.transcript}
              </div>
            ) : (
              <div>
                {!session.transcript && (
                  <p className="text-sm text-[#757169] mb-2">
                    {session.source === "calendly"
                      ? "Séance importée depuis Calendly. Collez la transcription (depuis Plaud) pour générer l'e-mail de suivi."
                      : "Collez la transcription de la séance pour générer l'e-mail de suivi."}
                  </p>
                )}
                <Textarea
                  data-testid="add-transcript-input"
                  value={transcriptDraft}
                  onChange={(e) => setTranscriptDraft(e.target.value)}
                  placeholder="Collez ici la transcription complète de la séance…"
                  className="bg-white rounded-xl min-h-[160px] leading-relaxed"
                />
                <div className="flex gap-2 mt-3">
                  <Button
                    onClick={saveTranscript}
                    disabled={savingTranscript}
                    data-testid="save-transcript-btn"
                    className="flex-1 rounded-xl bg-[#7B968B] hover:bg-[#6a8579] text-white"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {savingTranscript ? "Enregistrement…" : "Enregistrer la transcription"}
                  </Button>
                  {editingTranscript && (
                    <Button
                      variant="outline"
                      onClick={() => setEditingTranscript(false)}
                      data-testid="cancel-edit-transcript-btn"
                      className="rounded-xl border-[#EAE6DF] text-[#575349]"
                    >
                      Annuler
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-[#EAE6DF] p-6 shadow-[0_4px_20px_rgba(45,44,40,0.04)]">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-[#C07761]" strokeWidth={1.5} />
              <h3 className="font-heading font-medium text-[#2D2C28]">Génération de l'e-mail</h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#757169]">Modèle IA</Label>
                <Select value={model} onValueChange={setModel}>
                  <SelectTrigger data-testid="model-select" className="mt-1.5 bg-white rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MODELS.map((m) => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[#757169]">Langue de l'e-mail</Label>
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger data-testid="language-select" className="mt-1.5 bg-white rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LANGS.map((l) => (
                      <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[#757169]">Ton</Label>
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger data-testid="tone-select" className="mt-1.5 bg-white rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TONES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[#757169]">Longueur</Label>
                <Select value={length} onValueChange={setLength}>
                  <SelectTrigger data-testid="length-select" className="mt-1.5 bg-white rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LENGTHS.map((l) => (
                      <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="mt-3">
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-xs text-[#757169]">Instructions spécifiques (optionnel)</Label>
                <div className="flex items-center gap-2">
                  {templates.length > 0 && (
                    <Select value={selectedTemplate} onValueChange={applyTemplate}>
                      <SelectTrigger data-testid="template-select" className="bg-white rounded-lg h-8 text-xs w-44">
                        <SelectValue placeholder="Modèle…" />
                      </SelectTrigger>
                      <SelectContent>
                        {templates.map((t) => (
                          <SelectItem key={t.id} value={t.id}>
                            {t.name}{t.type === "texte" ? " · texte" : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  {selectedTemplate && (
                    <button
                      onClick={deleteTemplate}
                      data-testid="delete-template-btn"
                      title="Supprimer ce modèle"
                      className="p-1.5 rounded-lg text-[#C0BBB0] hover:text-[#B56B6B] hover:bg-[#B56B6B]/10 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button
                    onClick={saveAsTemplate}
                    data-testid="save-template-btn"
                    title="Enregistrer instructions + ton + longueur comme modèle réutilisable"
                    className="flex items-center gap-1 text-xs text-[#7B968B] hover:text-[#5e7a6e] font-medium transition-colors"
                  >
                    <Save className="w-3.5 h-3.5" /> Modèle
                  </button>
                </div>
              </div>
              <Textarea
                data-testid="instructions-input"
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder="Ex. rappeler le prochain rendez-vous, insister sur l'exercice de respiration…"
                className="bg-white rounded-xl min-h-[70px]"
              />
            </div>

            <Button
              onClick={generate}
              disabled={generating}
              data-testid="generate-email-btn"
              className="w-full mt-4 rounded-xl bg-[#C07761] hover:bg-[#A66350] text-white h-11"
            >
              <Wand2 className="w-4 h-4 mr-2" />
              {generating ? "Génération en cours…" : hasEmail ? "Régénérer l'e-mail" : "Générer l'e-mail"}
            </Button>
          </div>
        </div>

        {/* Right: email editor */}
        <div className="bg-white rounded-2xl border border-[#EAE6DF] p-6 shadow-[0_4px_20px_rgba(45,44,40,0.04)] flex flex-col">
          <h3 className="font-heading font-medium text-[#2D2C28] mb-4">E-mail au patient</h3>

          {!hasEmail && !generating ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center py-16">
              <div className="w-14 h-14 rounded-2xl bg-[#EAE6DF] flex items-center justify-center mb-4">
                <Sparkles className="w-7 h-7 text-[#C0BBB0]" strokeWidth={1.5} />
              </div>
              <p className="text-[#757169] max-w-xs">
                Configurez les options puis cliquez sur « Générer l'e-mail » pour créer un brouillon.
              </p>
            </div>
          ) : (
            <>
              <div className="mb-4">
                <Label className="text-xs text-[#757169]">Objet</Label>
                <Input
                  data-testid="email-subject-input"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="mt-1.5 bg-white rounded-xl font-medium"
                />
              </div>
              <div className="flex-1">
                <Label className="text-xs text-[#757169]">Message</Label>
                <div className="mt-1.5 email-editor bg-white rounded-xl" data-testid="email-body-input">
                  <ReactQuill
                    theme="snow"
                    value={body}
                    onChange={setBody}
                    modules={QUILL_MODULES}
                    formats={QUILL_FORMATS}
                  />
                </div>
              </div>

              {/* Attachments (from the therapist's computer, sent directly — no cloud storage) */}
              <div className="mt-5">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs text-[#757169] flex items-center gap-1.5">
                    <Paperclip className="w-3.5 h-3.5" /> Pièces jointes
                  </Label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    data-testid="attachment-file-input"
                    onChange={onFilePicked}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="upload-attachment-btn"
                    className="rounded-lg border-[#EAE6DF] text-[#575349] h-8 text-xs"
                  >
                    <Upload className="w-3.5 h-3.5 mr-1.5" /> Ajouter un fichier
                  </Button>
                </div>
                {attachments.length === 0 ? (
                  <p className="text-xs text-[#9c988f]">
                    Aucune pièce jointe. Les fichiers ajoutés depuis votre ordinateur sont envoyés directement, sans stockage externe.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {attachments.map((f, i) => (
                      <span
                        key={i}
                        data-testid={`attachment-chip-${i}`}
                        className="inline-flex items-center gap-1.5 bg-[#EAE6DF]/60 text-[#575349] rounded-full pl-3 pr-1.5 py-1 text-xs"
                      >
                        <Paperclip className="w-3 h-3" />
                        {f.name} <span className="text-[#9c988f]">({(f.size / 1024).toFixed(0)} Ko)</span>
                        <button
                          onClick={() => setAttachments((a) => a.filter((_, idx) => idx !== i))}
                          data-testid={`remove-attachment-${i}`}
                          className="hover:bg-[#d8d2c7] rounded-full p-0.5"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Recipients: Cc / Cci */}
              <div className="mt-5 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-[#757169]">Cc (copie visible)</Label>
                    <Input
                      data-testid="email-cc-input"
                      value={cc}
                      onChange={(e) => setCc(e.target.value)}
                      placeholder="partenaire@email.com, …"
                      className="mt-1.5 bg-white rounded-xl"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-[#757169]">Cci (copie cachée)</Label>
                    <Input
                      data-testid="email-bcc-input"
                      value={bcc}
                      onChange={(e) => setBcc(e.target.value)}
                      placeholder="confrere@email.com, …"
                      className="mt-1.5 bg-white rounded-xl"
                    />
                  </div>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    data-testid="copy-me-checkbox"
                    checked={copyMe}
                    onCheckedChange={(v) => setCopyMe(!!v)}
                  />
                  <span className="text-sm text-[#575349]">
                    Me mettre en copie ({user?.email})
                  </span>
                </label>
              </div>

              {!patient?.email && (
                <div className="flex items-center gap-2 mt-4 text-sm text-[#B7854f] bg-[#D4A373]/10 rounded-xl p-3">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  Ajoutez une adresse e-mail au patient pour pouvoir envoyer.
                </div>
              )}

              <div className="flex gap-3 mt-5">
                <Button
                  variant="outline"
                  onClick={saveDraft}
                  disabled={saving}
                  data-testid="save-draft-btn"
                  className="rounded-xl border-[#EAE6DF] text-[#575349] flex-1"
                >
                  <Save className="w-4 h-4 mr-2" /> {saving ? "…" : "Enregistrer"}
                </Button>
                <Button
                  onClick={() => setShowPreview(true)}
                  disabled={sending || !patient?.email}
                  data-testid="send-email-btn"
                  className="rounded-xl bg-[#7B968B] hover:bg-[#6a8579] text-white flex-1"
                >
                  <Send className="w-4 h-4 mr-2" /> Prévisualiser et envoyer
                </Button>
              </div>

              <EmailPreview
                open={showPreview}
                onOpenChange={setShowPreview}
                subject={subject}
                bodyHtml={body}
                signature={sender?.signature}
                fromName={sender?.from_name}
                fromEmail={sender?.from_email}
                to={patient?.email}
                ccList={ccPreview()}
                bccCount={bcc.split(",").map((e) => e.trim()).filter(Boolean).length}
                attachments={attachments}
                onSend={send}
                sending={sending}
              />
            </>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default SessionDetail;
